import { Link, List, ListItem } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { LinkTarget } from 'core/enums';
import { spaSendBlockLabelKeyMap } from 'quotes/constants';
import { ALL_SPA_AUDIENCE_RIGHTS, VendorQuoteHelper } from 'quotes/helpers';
import { quotePartsPath } from 'quotes/routes/routePaths';
import { ISpaAudienceRights, IVendorQuote } from 'quotes/types';

interface IProps {
  quotes: IVendorQuote[];
  withReason?: boolean;
  // The user's audience rights for Send as SPA; Download keeps the default, since it is not
  // refused for the audience.
  spaAudienceRights?: ISpaAudienceRights;
}

// Shared by both bulk warning dialogs (Send as SPA and SKU download), which listed quotes
// identically. Each item links to its quote (label = the displayed Quote ID, matching the VQ list
// column) alongside the vendor's quote number. The link opens in a new browser tab so the user
// keeps the grid multi-selection and the dialog. It lands on the parts page — the Quote Details
// panel's Attributes tab (where the missing values are set) is not URL-addressable.
//
// `withReason` appends why that specific quote is blocked. The batch can be held up by different
// quotes for different reasons, so one shared sentence above the list could name the wrong one.
export const BlockedQuoteList = ({
  quotes,
  withReason = false,
  spaAudienceRights = ALL_SPA_AUDIENCE_RIGHTS,
}: IProps) => {
  const { t } = useTranslation();

  return (
    <List marker="disc" sx={{ lineHeight: '21px' }}>
      {quotes.map((quote) => {
        const blockReason = withReason
          ? VendorQuoteHelper.getSpaSendBlockReason(quote, spaAudienceRights)
          : null;

        return (
          <ListItem key={quote.id}>
            <Link
              component={RouterLink}
              to={quotePartsPath(quote.id)}
              target={LinkTarget.BLANK}
              rel="noopener noreferrer"
              sx={{ fontWeight: 600 }}
            >
              {t('labelVendorQuoteWithId', { id: quote.id })}
            </Link>
            {quote.vendorQuoteNumber ? ` — ${quote.vendorQuoteNumber}` : ''}
            {blockReason ? ` — ${t(spaSendBlockLabelKeyMap[blockReason])}` : ''}
          </ListItem>
        );
      })}
    </List>
  );
};
